import { Outlet } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';

/**
 * Shared layout for all public marketing pages.
 *
 * Renders Navbar + page content + Footer. SEOHead is NOT included
 * because it requires per-page props (title, description, canonical).
 * Each page continues to render its own <SEOHead />.
 *
 * The Navbar already wraps content in RubyPublicAssistant, which
 * handles the Ruby launcher for marketing pages.
 *
 * Navbar-clearance contract (REK-1655). The marketing `<Navbar />` is
 * `fixed top-4` (16px from the viewport top) with `h-20` (80px tall),
 * so it floats with a translucent backdrop above page content from
 * y=16 to y=96. The layout intentionally does NOT add a top-padding
 * wrapper around `<Outlet />` -- doing so would push every section
 * (including its gradient/image background) DOWN by 112px, leaving a
 * blank band above the gradient where it visually appears "cut off"
 * (e.g. `/webinars`, `/partner-blog`, `/learn`, `/breaches` all have
 * gradient bands that need to extend behind the navbar).
 *
 * Instead, navbar clearance is owned by each hero / first-block
 * section via internal `pt-32` (128px). The CSS background (gradient,
 * image, color) of the `<section>` fills its FULL bounding box
 * INCLUDING the top padding, so the gradient extends to y=0 (under
 * the floating navbar) while the section's CONTENT (h1, pill eyebrow,
 * absolutely-positioned decorative cards) sits at y=128, with a
 * comfortable 32px gap below the navbar's bottom edge.
 *
 * Hard rule: every hero / page-shell hero / first-block surface on a
 * marketing route MUST declare `pt-32` (or equivalent ~128px top
 * padding) on its outer section so its content clears the floating
 * navbar. See `docs/context/FEATURE_SPECS/public-marketing-routes-and-auth.md`
 * for the standardization list. Adding pages without this padding
 * re-introduces the REK-1655 overlap bug.
 *
 * Next.js mapping: app/(marketing)/layout.tsx
 */
export function MarketingLayout() {
  return (
    <>
      <Navbar />
      <Outlet />
      <Footer />
    </>
  );
}
